import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/shared/lib/api/api-client'
import {
  bulkClassifyTransactions,
  createTag,
  deleteTag,
  fetchCategories,
  fetchTags,
  fetchTagsOverview,
  fetchTransactionClassification,
  setTransactionClassification,
  updateTag,
} from '../api/tagging-api'
import type { BulkClassifyInput, CreateTagInput, SetClassificationInput } from '../types/tagging'

// The Transactions grid shows each row's category (UC-084): it is refetched after any classification.
const TRANSACTIONS_LIST_KEY = ['Transactions', 'list'] as const

export const taggingKeys = {
  categories: ['tagging', 'categories'] as const,
  tags: ['tagging', 'tags'] as const,
  tagsOverview: ['tagging', 'tags', 'overview'] as const,
  classification: (transactionId: number) => ['tagging', 'classification', transactionId] as const,
  classifications: ['tagging', 'classification'] as const,
}

// The catalogue only changes with a deploy.
const CATALOGUE_STALE_MS = 60 * 60 * 1000

export function useCategories() {
  return useQuery({
    queryKey: taggingKeys.categories,
    queryFn: ({ signal }) => fetchCategories(signal),
    staleTime: CATALOGUE_STALE_MS,
  })
}

export function useTags() {
  return useQuery({
    queryKey: taggingKeys.tags,
    queryFn: ({ signal }) => fetchTags(signal),
    staleTime: 60 * 1000,
  })
}

/** ADR-011: fetched by id every time the panel opens, never rebuilt from the grid row. */
export function useTransactionClassification(transactionId: number | null) {
  const query = useQuery({
    queryKey: taggingKeys.classification(transactionId ?? 0),
    queryFn: ({ signal }) => fetchTransactionClassification(transactionId as number, signal),
    enabled: transactionId != null,
    staleTime: 0,
  })
  const isNotFound = query.error instanceof ApiError && query.error.status === 404
  return { ...query, isNotFound }
}

export function useCreateTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTagInput) => createTag(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taggingKeys.tags }),
  })
}

/** The tags admin screen (UC-087): active tags with their transactions and rules. */
export function useTagsOverview() {
  return useQuery({ queryKey: taggingKeys.tagsOverview, queryFn: ({ signal }) => fetchTagsOverview(signal) })
}

/** Rename, recolour or delete a tag. Pickers, rules, panels and the grid all show tag names, so all refresh. */
export function useTagActions() {
  const queryClient = useQueryClient()
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: taggingKeys.tags }),
      queryClient.invalidateQueries({ queryKey: ['tagging', 'rules'] }),
      queryClient.invalidateQueries({ queryKey: taggingKeys.classifications }),
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_LIST_KEY }),
    ])
  const update = useMutation({
    mutationFn: ({ tagId, input }: { tagId: number; input: CreateTagInput }) => updateTag(tagId, input),
    onSuccess: refresh,
  })
  const remove = useMutation({ mutationFn: (tagId: number) => deleteTag(tagId), onSuccess: refresh })
  return { update, remove }
}

export function useSetClassification(transactionId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SetClassificationInput) => setTransactionClassification(transactionId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(taggingKeys.classification(transactionId), saved)
      return queryClient.invalidateQueries({ queryKey: TRANSACTIONS_LIST_KEY })
    },
  })
}

export function useBulkClassify() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BulkClassifyInput) => bulkClassifyTransactions(input),
    // Any open panel may show one of the transactions just changed.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taggingKeys.classifications }),
        queryClient.invalidateQueries({ queryKey: TRANSACTIONS_LIST_KEY }),
      ]),
  })
}

/** The backend's message for a failed tagging call, or a generic fallback. */
export function taggingErrorMessage(error: unknown, fallback: string): string {
  return (error instanceof ApiError ? error.errors[0] : undefined) ?? fallback
}
